import React, { useState, useEffect } from 'react';
import { getAllTopics, addTopic, updateTopic, deleteTopic } from '../../firebase/topicsService';
import './TopicsManagement.css';

function TopicsManagement() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState(null);

  // دالة لتحويل التاريخ الميلادي إلى هجري
  const formatHijriDate = (timestamp) => {
    if (!timestamp) return '-';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      
      // استخدام Intl.DateTimeFormat للحصول على التاريخ الهجري الدقيق
      const formatter = new Intl.DateTimeFormat('ar-SA-u-ca-islamic', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      return formatter.format(date);
    } catch (error) {
      console.error('Error formatting Hijri date:', error);
      return '-';
    }
  };

  useEffect(() => {
    loadTopics();
  }, []);

  const loadTopics = async () => {
    setLoading(true);
    try {
      const topicsData = await getAllTopics();
      setTopics(topicsData);
    } catch (error) {
      console.error('Error loading topics:', error);
      setError('فشل تحميل المواضيع');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title || !formData.content) {
      setError('جميع الحقول مطلوبة');
      return;
    }

    try {
      if (editingTopic) {
        await updateTopic(editingTopic.id, formData);
        setSuccess('تم تحديث الموضوع بنجاح');
      } else {
        await addTopic(formData);
        setSuccess('تم إضافة الموضوع بنجاح');
      }
      
      await loadTopics();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving topic:', error);
      setError('فشل حفظ الموضوع');
    }
  };

  const handleEdit = (topic) => {
    setEditingTopic(topic);
    setFormData({
      title: topic.title,
      content: topic.content
    });
    setShowAddModal(true);
  };

  const handleDeleteClick = (topic) => {
    setTopicToDelete(topic);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!topicToDelete) return;

    setDeleting(topicToDelete.id);
    setShowDeleteModal(false);
    
    try {
      await deleteTopic(topicToDelete.id);
      await loadTopics();
      setSuccess('تم حذف الموضوع بنجاح');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting topic:', error);
      setError('فشل حذف الموضوع');
    } finally {
      setDeleting(null);
      setTopicToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTopicToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: ''
    });
    setEditingTopic(null);
    setShowAddModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div className="loading">جاري التحميل...</div>;
  }

  return (
    <div className="topics-management">
      <div className="topics-header">
        <h2>إدارة المواضيع</h2>
        <button className="add-topic-btn" onClick={() => setShowAddModal(true)}>
          + إضافة موضوع جديد
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="topics-stats">
        <div className="stat-card">
          <div className="stat-number">{topics.length}</div>
          <div className="stat-label">إجمالي المواضيع</div>
        </div>
      </div>

      <div className="topics-table">
        <table>
          <thead>
            <tr>
              <th>العنوان</th>
              <th>تاريخ النشر</th>
              <th>المشاهدات</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {topics.map(topic => (
              <tr key={topic.id}>
                <td className="topic-title-cell">
                  <div className="title-with-delete">
                    <button 
                      className="delete-btn-icon" 
                      onClick={() => handleDeleteClick(topic)}
                      disabled={deleting === topic.id}
                      title="حذف الموضوع"
                    >
                      {deleting === topic.id ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinner">
                          <line x1="12" y1="2" x2="12" y2="6"></line>
                          <line x1="12" y1="18" x2="12" y2="22"></line>
                          <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                          <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                          <line x1="2" y1="12" x2="6" y2="12"></line>
                          <line x1="18" y1="12" x2="22" y2="12"></line>
                          <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                          <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      )}
                    </button>
                    <span className="topic-title-text">{topic.title}</span>
                  </div>
                </td>
                <td>{formatHijriDate(topic.createdAt || topic.updatedAt)}</td>
                <td>{topic.views || 0}</td>
                <td className="actions-cell">
                  <button 
                    className="edit-btn" 
                    onClick={() => handleEdit(topic)}
                  >
                    تعديل
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {topics.length === 0 && (
          <div className="no-topics">لا توجد مواضيع حالياً</div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTopic ? 'تعديل الموضوع' : 'إضافة موضوع جديد'}</h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>عنوان الموضوع *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الموضوع"
                  required
                />
              </div>


              <div className="form-group">
                <label>محتوى الموضوع *</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="أدخل محتوى الموضوع كاملاً"
                  rows="15"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={resetForm}>
                  إلغاء
                </button>
                <button type="submit" className="submit-btn">
                  {editingTopic ? 'تحديث الموضوع' : 'إضافة الموضوع'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* مودال تأكيد الحذف */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3 className="delete-modal-title">تأكيد الحذف</h3>
            <p className="delete-modal-message">
              هل أنت متأكد من حذف الموضوع:<br />
              <strong>"{topicToDelete?.title}"</strong>؟<br />
              لا يمكن التراجع عن هذا الإجراء.
            </p>
            <div className="delete-modal-actions">
              <button className="cancel-delete-btn" onClick={cancelDelete}>
                إلغاء
              </button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>
                حذف الموضوع
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TopicsManagement;
